/**
 * Imports store — two Context providers mirroring the Reports +
 * Dashboards pattern.
 *
 *   - `ImportedDatasetsProvider` keeps the catalogue of imports
 *     (one row per imported file) in `divineiot:imports`.
 *
 *   - `ImportedRowsProvider` keeps the actual mapped rows, chunked
 *     under `divineiot:import-rows:{dataset_id}` (one localStorage
 *     key per dataset). Rows aren't loaded into memory until a
 *     consumer asks for them via `byDataset(id)` — keeps the hot
 *     path light when many datasets exist but only one is active.
 *
 * Mutation API mirrors `DashboardsProvider`: `create`, `remove`,
 * `clear`. Imports are conceptually immutable after commit — there's
 * no `update`; users delete + re-import to fix mappings.
 *
 * Phase-1 swap: replace localStorage with `/v1/twin/imports` CRUD.
 */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { ImportedDataset, ImportedRow } from "./types";
import { isLiveApi } from "@/lib/api";

// ────────────────────────────────────────────────────────────────────
// Imported datasets — the catalogue
// ────────────────────────────────────────────────────────────────────

const DATASETS_STORAGE_KEY = "divineiot:imports";

export interface ImportedDatasetsState {
  datasets: ImportedDataset[];
  byId: (id: string) => ImportedDataset | undefined;

  /** Add a dataset; returns its id. */
  create: (
    dataset: Omit<ImportedDataset, "id" | "importedAt" | "owner"> & {
      id?: string;
      owner?: string;
    },
  ) => string;

  /** Delete a dataset + its rows. */
  remove: (id: string) => void;

  /** Clear all imports (admin reset). */
  clear: () => void;

  /** Mutation counter. */
  version: number;
}

const ImportedDatasetsContext = createContext<ImportedDatasetsState | null>(null);

export function useOptionalImportedDatasets(): ImportedDatasetsState | null {
  return useContext(ImportedDatasetsContext);
}

function readDatasets(): ImportedDataset[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(DATASETS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as ImportedDataset[]) : [];
  } catch {
    return [];
  }
}

function writeDatasets(datasets: ImportedDataset[]) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(DATASETS_STORAGE_KEY, JSON.stringify(datasets));
  } catch {
    /* localStorage full — drop silently, datasets stay in-memory */
  }
}

// ────────────────────────────────────────────────────────────────────
// Imported rows — chunked storage, one key per dataset
// ────────────────────────────────────────────────────────────────────

const ROWS_STORAGE_PREFIX = "divineiot:import-rows:";

export interface ImportedRowsState {
  /** Lookup rows by dataset id. Returns [] if not loaded yet. */
  byDataset: (datasetId: string) => ImportedRow[];

  /** Persist a freshly-imported rowset. */
  set: (datasetId: string, rows: ImportedRow[]) => void;

  /** Drop rows for a dataset (called from `ImportedDatasetsState.remove`). */
  remove: (datasetId: string) => void;

  /** Mutation counter. */
  version: number;
}

const ImportedRowsContext = createContext<ImportedRowsState | null>(null);

export function useImportedRows(): ImportedRowsState {
  const ctx = useContext(ImportedRowsContext);
  if (!ctx) {
    throw new Error(
      "useImportedRows must be used inside <ImportedRowsProvider>",
    );
  }
  return ctx;
}

export function useOptionalImportedRows(): ImportedRowsState | null {
  return useContext(ImportedRowsContext);
}

function readRows(datasetId: string): ImportedRow[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(ROWS_STORAGE_PREFIX + datasetId);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as ImportedRow[]) : [];
  } catch {
    return [];
  }
}

function writeRows(datasetId: string, rows: ImportedRow[]) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      ROWS_STORAGE_PREFIX + datasetId,
      JSON.stringify(rows),
    );
  } catch {
    /* Spill to IndexedDB is a future enhancement; today rows beyond
     * localStorage budget stay in-memory only — the user sees the
     * report render once and we drop the disk persistence. */
  }
}

function deleteRows(datasetId: string) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(ROWS_STORAGE_PREFIX + datasetId);
  } catch {
    /* swallow */
  }
}

// ────────────────────────────────────────────────────────────────────
// Combined provider — mount both contexts. Mutations stay coordinated
// (deleting a dataset also drops its rows).
// ────────────────────────────────────────────────────────────────────

export function ImportsProvider({ children }: { children: ReactNode }) {
  // Live mode: seed empty + never touch the (global, non-org-scoped) localStorage
  // keys, so one org's imports can't bleed into the next org on switch (#68).
  // Stub mode keeps the localStorage-backed demo behaviour.
  const [datasets, setDatasets] = useState<ImportedDataset[]>(() =>
    isLiveApi ? [] : readDatasets(),
  );
  const [datasetsVersion, setDatasetsVersion] = useState(0);

  // Rows: hold an in-memory map for fast lookup; lazy-load from localStorage
  // on first access per dataset.
  const [rowsByDataset, setRowsByDataset] = useState<Record<string, ImportedRow[]>>({});
  const [rowsVersion, setRowsVersion] = useState(0);

  useEffect(() => {
    if (!isLiveApi) writeDatasets(datasets);
  }, [datasets]);

  // ── Datasets API ────────────────────────────────────────────────
  const datasetsState = useMemo<ImportedDatasetsState>(() => {
    const byId = (id: string) => datasets.find((d) => d.id === id);

    const create: ImportedDatasetsState["create"] = (seed) => {
      const id =
        seed.id ?? `ds-${Date.now()}-${Math.floor(Math.random() * 10_000)}`;
      const next: ImportedDataset = {
        id,
        filename: seed.filename,
        schemaId: seed.schemaId,
        mappingConfig: seed.mappingConfig,
        rowCount: seed.rowCount,
        importedAt: new Date().toISOString(),
        batchImportId: seed.batchImportId,
        parseWarnings: seed.parseWarnings,
        sourceType: seed.sourceType,
        dateRange: seed.dateRange,
        owner: seed.owner ?? "you",
      };
      setDatasets((prev) => [next, ...prev]);
      setDatasetsVersion((v) => v + 1);
      return id;
    };

    const remove: ImportedDatasetsState["remove"] = (id) => {
      setDatasets((prev) => prev.filter((d) => d.id !== id));
      // Cascade delete the rows in the same tick.
      setRowsByDataset((prev) => {
        const { [id]: _removed, ...rest } = prev;
        return rest;
      });
      deleteRows(id);
      setDatasetsVersion((v) => v + 1);
      setRowsVersion((v) => v + 1);
    };

    const clear: ImportedDatasetsState["clear"] = () => {
      for (const d of datasets) deleteRows(d.id);
      setDatasets([]);
      setRowsByDataset({});
      setDatasetsVersion((v) => v + 1);
      setRowsVersion((v) => v + 1);
    };

    return {
      datasets,
      byId,
      create,
      remove,
      clear,
      version: datasetsVersion,
    };
  }, [datasets, datasetsVersion]);

  // ── Rows API ────────────────────────────────────────────────────
  const rowsState = useMemo<ImportedRowsState>(() => {
    const byDataset = (datasetId: string): ImportedRow[] => {
      if (rowsByDataset[datasetId]) return rowsByDataset[datasetId];
      // Lazy-load from localStorage on first miss — stub only; the rows key is
      // global (not org-scoped), so reading it in live mode would leak (#68).
      const loaded = isLiveApi ? [] : readRows(datasetId);
      if (loaded.length > 0) {
        // Cache for subsequent calls. We can't setState during render,
        // but the next read will hit the cache via the effect below.
        // For now, return the loaded value directly.
        rowsByDataset[datasetId] = loaded;
      }
      return loaded;
    };

    const set: ImportedRowsState["set"] = (datasetId, rows) => {
      setRowsByDataset((prev) => ({ ...prev, [datasetId]: rows }));
      if (!isLiveApi) writeRows(datasetId, rows);
      setRowsVersion((v) => v + 1);
    };

    const remove: ImportedRowsState["remove"] = (datasetId) => {
      setRowsByDataset((prev) => {
        const { [datasetId]: _removed, ...rest } = prev;
        return rest;
      });
      deleteRows(datasetId);
      setRowsVersion((v) => v + 1);
    };

    return {
      byDataset,
      set,
      remove,
      version: rowsVersion,
    };
  }, [rowsByDataset, rowsVersion]);

  return (
    <ImportedDatasetsContext.Provider value={datasetsState}>
      <ImportedRowsContext.Provider value={rowsState}>
        {children}
      </ImportedRowsContext.Provider>
    </ImportedDatasetsContext.Provider>
  );
}
